import { action, actionOn, thunk } from "easy-peasy";
import axios from '../services/axios';

const videosInitialState = {
    propsFetched: {},
    allVideos: [],
    searchedVideosUUIDS: [],
    videoById: null,
    isFetchingAllVideos : false,
    isFetchingSearchedVideosUUIDS: false,
    isGradualFetchingAllVideos : false,
    isGradualFetchVideosNecessary: true,
    isUpdateNecessary: false,
    initialFetchDone: false,
    isFetchingOneVideo: false,
    isFetchingMediaAndConversionProp: false
}

const videos = {
    ...videosInitialState, 

    onGlobalResetCalled: actionOn(
        (actions, storeActions) => [
            storeActions.resetStore.performReset,
        ], 
        (state, target) => {
            const [performReset] = target.resolvedTargets;
            
            if (target.type === performReset) {
                ({
                    propsFetched: state.propsFetched,
                    allVideos: state.allVideos,
                    searchedVideosUUIDS: state.searchedVideosUUIDS,
                    videoById: state.videoById,
                    isFetchingAllVideos: state.isFetchingAllVideos,
                    isFetchingSearchedVideosUUIDS: state.isFetchingSearchedVideosUUIDS,
                    isGradualFetchVideosNecessary: state.isGradualFetchVideosNecessary,
                    isGradualFetchingAllVideos: state.isGradualFetchingAllVideos,
                    isUpdateNecessary: state.isUpdateNecessary,
                    initialFetchDone: state.initialFetchDone,
                    isFetchingOneVideo: state.isFetchingOneVideo,
                    isFetchingMediaAndConversionProp: state.isFetchingMediaAndConversionProp,
                } = videosInitialState);
            }
        }
        ),
        
        
    // handling all videos
    setInitialFetchDone: action((state, payload) => {
       state.initialFetchDone = payload; 
    }),
        
    setIsUpdateNecessary: action((state, payload) => {
        state.isUpdateNecessary = payload;
    }),
    setIsGradualFetchVideosNecessary: action((state, payload) => {
        state.isGradualFetchVideosNecessary = payload;
    }),
    setPropsFetched: action((state, payload) => {
        state.propsFetched[payload] = true;
    }),
    setAllVideos: action((state, payload) => {
        state.allVideos = payload;
    }),
    setIsFetchingAllVideos: action((state, payload) => {
        state.isFetchingAllVideos = payload;
    }),
    setIsGradualFetchingAllVideos: action((state, payload) => {
        state.isGradualFetchingAllVideos = payload;
    }),

    fetchVideos: thunk((actions, payload, helpers) => {
        const { isFetchingAllVideos, isUpdateNecessary } = helpers.getState();
        const {apiData} = helpers.getStoreState().actionSlugs;

        const getAllUrl = apiData.ACTIONS?.videos?.readAll?.url;
        if( !isFetchingAllVideos && isUpdateNecessary) {
           actions.setIsFetchingAllVideos(true);
            return axios.get(getAllUrl)
                .then((data) => {
                    actions.setAllVideos(data);
                    actions.setIsFetchingAllVideos(videosInitialState.isFetchingAllVideos);
                    actions.setIsUpdateNecessary(videosInitialState.isUpdateNecessary);
                    return data;
                })
                .catch(error => {
                    return Promise.reject(error)
                });
        }
        else {
            return Promise.resolve();
        }
    }),

    updateVideosProps: thunk((actions, payload, helpers) => {
        const allVideos = helpers.getState().allVideos;
      
        const newAllVideos = allVideos.map(oldVideo => {
            const newVideo = payload.find(video => video.uniqueId === oldVideo.uniqueId);
            return { ...oldVideo, ...newVideo }
        });
        actions.setAllVideos(newAllVideos);
    }),

    fetchVideosProps: thunk((actions, payload) => {
        const prop = payload;

        if (prop?.url) {
            return axios.get(prop?.url)
                .then(data => {
                    actions.updateVideosProps(data);
                    actions.setPropsFetched(prop?.name);
                    return data;
                })
                .catch((error) => {
                    return Promise.reject(error);
                })
        }
    }),


    fetchAllPropsForAllVideos: thunk((actions) => {
        return Promise.all([
            actions.gradualFetchVideosData({ props: "getPropsForAll", alwaysFetch: false }),
            actions.gradualFetchVideosData({ props: "getConversionsPropsForAll", alwaysFetch: true })]);
    }),


    gradualFetchVideosData: thunk((actions, payload, helpers) => {
        const { apiData } = helpers.getStoreState().actionSlugs;
        const { propsFetched, isGradualFetchVideosNecessary } = helpers.getState();
        const { props, alwaysFetch } = payload;

        if ((isGradualFetchVideosNecessary || alwaysFetch) && apiData.ACTIONS.videos) {

            const propsForAll = apiData.ACTIONS.videos[props];
            const gradualAccessRoutes = propsForAll 
                ? Object.keys(propsForAll).map(keyProp => {
                        return {
                            name: keyProp,
                            url: propsForAll[keyProp].url,
                        };
                    })
                : [];
            if(gradualAccessRoutes.length > 0) {
                let gradualFetchToBeDone = [];
                gradualAccessRoutes.forEach(prop => {
                    if (propsFetched[prop?.name] === undefined || alwaysFetch) {
                        gradualFetchToBeDone.push(actions.fetchVideosProps(prop));
                    }
                });
                return Promise.all(gradualFetchToBeDone).then(function(results) {
                    actions.setIsGradualFetchVideosNecessary(false);
               })
            } else {
                return Promise.resolve();
            }
        } else {
            return Promise.resolve();
        }
    }),

    // ACTIONS TO HYDRATE STORE INSTEAD OF REFETCHING LIST OF VIDEOS

    addVideo: action((state, payload) => {
        state.allVideos.push(payload);
    }),

    updateAllVideos: action((state, payload) => {
        const index = state.allVideos.findIndex((video) => video.uniqueId === payload.uniqueId);

        state.allVideos[index] = {
            ...state.allVideos[index],
            ...payload,
        };

    }),

    removeFromAllVideos: action((state, payload) => {
        const indexToRemove = state.allVideos.findIndex(video => video.uniqueId === payload);
        state.allVideos.splice(indexToRemove, 1);
    }),

    // handling one video
    setVideoById: action((state, payload) => {
        state.videoById = payload;
    }),
    setIsFetchingOneVideo: action((state, payload) => {
        state.isFetchingOneVideo = payload;
    }),
    setIsFetchingMediaAndConversionProp: action((state, payload) => {
        state.isFetchingMediaAndConversionProp = payload;
    }),

    fetchVideoById: thunk((actions, payload, helpers) => {
        const { isFetchingOneVideo} = helpers.getState();
        const apiData = helpers.getStoreState().actionSlugs.apiData;


        const getOneUrl = apiData.ACTIONS['videos']['readOne'].url.replace('uniqueId', payload);

        if (!isFetchingOneVideo) {
            actions.setIsFetchingOneVideo(true);
            actions.setVideoById(null);

            return axios.get(getOneUrl)
                .then((data) => {
                    actions.setIsFetchingOneVideo(videosInitialState.isFetchingOneVideo);
                    actions.setVideoById(data);
                })
                .catch(error => Promise.reject(error));
        } else {
            return Promise.resolve();
        }
    }),


    // posting one video
    postVideo: thunk((actions, payload, helpers) => { 
        const apiData = helpers.getStoreState().actionSlugs.apiData;


        const createVideoUrl = apiData.ACTIONS?.videos?.create?.url;
        const { formData } = payload;

        return axios.post(createVideoUrl, formData)
        .then((result) => {
            actions.addVideo(result);
            actions.setVideoById(videosInitialState.videoById);
            return result;
        }).catch(e => {
            return Promise.reject(e);
        });
    }),

    // update one video
    updateVideo: thunk((actions, payload, helpers) => {
        const { formData, uniqueId } = payload;
        const apiData = helpers.getStoreState().actionSlugs.apiData;
        
        const updateVideoUrl = apiData.ACTIONS['videos']['update'].url.replace('uniqueId', uniqueId);

        return axios.post(updateVideoUrl, formData )
            .then((result) => {
                actions.updateAllVideos(result);
                actions.setVideoById(videosInitialState.videoById)
                return result;
            })
            .catch(error => {
                return Promise.reject(error);
            })
    }),

    // add video's length information
    addVideoLength: thunk((actions, payload, helpers) => {
        const { uniqueId, videoLength } = payload;
        const apiData = helpers.getStoreState().actionSlugs.apiData;
        
        const addVideoLengthUrl = apiData.ACTIONS['videos']['addLength'].url.replace('uniqueId', uniqueId);

        return axios.post(addVideoLengthUrl, { videoLength: videoLength })
        .then((result) => {
            actions.updateAllVideos(result);
            return result;
        })
        .catch(error => {
            return Promise.reject(error);
        })
    }),

    editVideoRomes: thunk((actions, payload, helpers) => {
        const { formData, uniqueId } = payload;
        const apiData = helpers.getStoreState().actionSlugs.apiData;

        const editVideoROMEUrl = apiData.ACTIONS['videos']['editRome'].url.replace('uniqueId', uniqueId);
        return axios.post(editVideoROMEUrl, formData)
            .then((result) => {
                actions.updateAllVideos(result);
                actions.setVideoById(result);
                return result;
            })
            .catch(error => {
                return Promise.reject(error);
            })
    }),

    addVideoPro: thunk((actions, payload, helpers) => {
        const { formData, uniqueId } = payload;
        const apiData = helpers.getStoreState().actionSlugs.apiData;

        const addVideoProsUrl = apiData.ACTIONS['videos']['addPro'].url.replace('uniqueId', uniqueId);
        return axios.post(addVideoProsUrl, formData)
            .then((result) => {
                actions.updateAllVideos(result);
                actions.setVideoById(result);
                return result;
            })
            .catch(error => {
                return Promise.reject(error);
            })
    }),

    editVideoPro: thunk((actions, payload, helpers) => {
        const { formData, uniqueId } = payload;
        const apiData = helpers.getStoreState().actionSlugs.apiData;
        const { videoById } = helpers.getState();
        const { allProfessionals } = helpers.getStoreState().romes;
        const { setAllProfessionals } = helpers.getStoreActions().romes;

        const editVideoProsUrl = apiData.ACTIONS['professionals']['update'].url.replace('uniqueId', uniqueId);
        return axios.post(editVideoProsUrl, formData)
            .then((result) => {
                const updatedPros = videoById.pros.map(pro => 
                    pro.uniqueId === result.uniqueId ? result : pro
                );
                // Remove rome if not used anymore
                const updatedRomesFromPro = videoById.romesFromPro?.filter(rome =>
                    updatedPros.some(pro => pro.mainJob?.rome && pro.mainJob?.rome === rome)
                );
                // Add rome if not already exist
                if (result.mainJob?.rome &&
                    !updatedRomesFromPro.some(romeFromPro => romeFromPro === result.mainJob?.rome)) {
                    updatedRomesFromPro.push(result.mainJob.rome);
                }
                const updatedVideoById = {
                    ...videoById,
                    romesFromPro: updatedRomesFromPro,
                    pros: updatedPros
                };
                actions.setVideoById(updatedVideoById);

                const updatedAllPros = [...allProfessionals]?.map(pro => pro.uniqueId === result?.uniqueId ? result : pro);
                setAllProfessionals(updatedAllPros);

                return result;
            })
            .catch(error => {
                return Promise.reject(error);
            })
    }),

    editVideoAssignPro: thunk((actions, payload, helpers) => {
        const { formData, uniqueId } = payload;
        const apiData = helpers.getStoreState().actionSlugs.apiData;

        const editVideoAssignProUrl = apiData.ACTIONS['videos']['assignPro'].url.replace('uniqueId', uniqueId);
        return axios.post(editVideoAssignProUrl, formData)
            .then((result) => {
                actions.updateAllVideos(result);
                actions.setVideoById(result);
                return result;
            })
            .catch(error => {
                return Promise.reject(error);
            })
    }),

    editVideoCommentM360: thunk((actions, payload, helpers) => {
        const { formData, uniqueId } = payload;
        const apiData = helpers.getStoreState().actionSlugs.apiData;

        const editVideoCommentM360Url = apiData.ACTIONS['videos']['editCommentM360'].url.replace('uniqueId', uniqueId);
        return axios.post(editVideoCommentM360Url, formData)
            .then((result) => {
                actions.updateAllVideos(result);
                actions.setVideoById(result);
                return result;
            })
            .catch(error => {
                return Promise.reject(error);
            })
    }),


    editVideoAddMedia: thunk((actions, payload, helpers) => {
        const { formData, uniqueId } = payload;
        const apiData = helpers.getStoreState().actionSlugs.apiData;

        const editVideoAddMediaUrl = apiData.ACTIONS?.videos?.addOriginalMedia?.url?.replace('uniqueId', uniqueId);
        return axios.post(editVideoAddMediaUrl, formData)
            .then((result) => {
                actions.updateAllVideos(result);
                actions.setVideoById(result);
                return result;
            })
            .catch(error => {
                return Promise.reject(error);
            })
    }),


    // deleting one video
    deleteVideo: thunk((actions, payload, helpers) => {
        const { uniqueId } = payload;
        const apiData = helpers.getStoreState().actionSlugs.apiData;
        
        const formData = new FormData();
        formData.set(`${apiData.ACTIONS['videos']['delete'].formName}[_token]`, apiData.ACTIONS['videos']['delete'].csrfToken)
        const deleteVideoUrl = apiData.ACTIONS['videos']['delete'].url.replace('uniqueId', uniqueId);


        return axios.post(deleteVideoUrl, formData)
        .then(result => {
            actions.setVideoById(videosInitialState.videoById);
            actions.removeFromAllVideos(uniqueId);
            const { setIsUpdateNecessary:setPlaylistsIsUpdateNecessary } = helpers.getStoreActions().playlists;
            setPlaylistsIsUpdateNecessary(true);
        })
        .catch(error => {
            return Promise.reject(error)
        });
    }),

    // delete pro from video
    deleteProFromVideo: thunk((actions, payload, helpers) => {
        const { videoUniqueId, proUniqueId } = payload;
        const apiData = helpers.getStoreState().actionSlugs.apiData;
        const formData = new FormData();
        formData.set(`${apiData.ACTIONS['videos']['deletePro'].formName}[_token]`, apiData.ACTIONS['videos']['deletePro'].csrfToken)
        const deleteProFromVideoUrl = apiData.ACTIONS['videos']['deletePro'].url
            .replace('videoUniqueId', videoUniqueId)
            .replace('proUniqueId', proUniqueId);


        return axios.post(deleteProFromVideoUrl, formData)
            .then(result => {
                actions.updateAllVideos(result);
                actions.setVideoById(result);
                return Promise.resolve(result);
            })
            .catch(error => {
                return Promise.reject(error)
            });
    }),


    updateVideoFiles: thunk((actions, payload, helpers) => {
        const { formData, uniqueId } = payload;
        const apiData = helpers.getStoreState().actionSlugs.apiData;

        const editVideoFilesUrl = apiData.ACTIONS['videos']['editFiles'].url.replace('uniqueId', uniqueId);
        return axios.post(editVideoFilesUrl, formData)
            .then((result) => {
                actions.updateAllVideos(result);
                actions.setVideoById(result);
                return result;
            })
            .catch(error => {
                return Promise.reject(error);
            })
    }),

    fetchVideoMediasAndConversions: thunk((actions, payload, helpers) => {

        const { uniqueId } = payload;
        const { isFetchingMediaAndConversionProp } = helpers.getState();
        const { videoById } = helpers.getState();

        const apiData = helpers.getStoreState().actionSlugs.apiData;
        const getUrl = apiData.ACTIONS['videos']?.['getProps']?.['getMediasAndConversions']?.url.replace('uniqueId', uniqueId);

        if (!isFetchingMediaAndConversionProp) {
            actions.setIsFetchingMediaAndConversionProp(true);
            return axios.get(getUrl)
                .then((result) => {
                    if (result?.uniqueId == videoById?.uniqueId) {
                        const newVideoById = {
                            ...videoById,
                            ...result
                        }
                        actions.setVideoById(newVideoById);
                    }
                    return result;
                })
                .catch(error => {
                    return Promise.reject(error);
                })
                .finally(() => actions.setIsFetchingMediaAndConversionProp(false));
    }}),

    setSearchedVideosUUIDS: action((state, payload) => {
        state.searchedVideosUUIDS = payload;
    }),
    setIsFetchingSearchedVideosUUIDS: action((state, payload) => {
        state.isFetchingSearchedVideosUUIDS = payload;
    }),
    searchVideos: thunk((actions, payload, helpers) => {
        const { apiData } = helpers.getStoreState().actionSlugs;

        const searchUrl = apiData.ACTIONS?.videos?.search?.url;

        actions.setIsFetchingSearchedVideosUUIDS(true);
        actions.setSearchedVideosUUIDS(videosInitialState.searchedVideosUUIDS);
        return axios.post(searchUrl, payload)
            .then((data) => {
                actions.setSearchedVideosUUIDS(data);
                actions.setIsFetchingSearchedVideosUUIDS(videosInitialState.isFetchingSearchedVideosUUIDS);
                return data;
            })
            .catch(error => {
                return Promise.reject(error)
            });
    }),

    updateVideoPlaylists: thunk((actions, payload, helpers) => {
        const { apiData } = helpers.getStoreState().actionSlugs;
        const { playlistById } = helpers.getStoreState().playlists;
        const { updateAllPlaylists, setPlaylistById } = helpers.getStoreActions().playlists;
        const { playlistsUUIDS, uniqueId } = payload;

        const url = apiData.ACTIONS?.videos?.updatePlaylists?.url?.replace('uniqueId', uniqueId);

        return axios.post(url, { playlistsUUIDS: playlistsUUIDS })
            .then((data) => {
                if (data.playlists?.length > 0) {
                    updateAllPlaylists(data.playlists);
                    const updatedPlaylistById = data.playlists
                        .find(playlist => playlist.uniqueId === playlistById?.uniqueId);
                    if(updatedPlaylistById) {
                        setPlaylistById(updatedPlaylistById);
                    }
                }
                if (data.video) {
                    actions.updateAllVideos(data.video);
                    actions.setVideoById(data.video);
                }
                return data;
            })
            .catch(error => {
                return Promise.reject(error)
            });
    }),

}

export default videos;